// src/errors/utils/error-parser.ts

/**
 * Error Parser Utility
 *
 * WHY: Extract structured information from various error types
 * WHAT: Parses axios errors, native errors, and custom errors
 * BENEFIT:
 *   - Consistent error data extraction
 *   - Type-safe error handling
 *   - Handles edge cases and malformed errors
 *
 * DESIGN DECISIONS:
 * 1. Type guards for safe error identification
 * 2. Graceful handling of unknown error shapes
 * 3. Extraction of all useful information
 */

import { AxiosError } from "axios";
import { type ApiErrorResponse, isApiErrorResponse } from "../types";

/**
 * Type guard for Axios errors
 */
export const isAxiosError = (error: unknown): error is AxiosError => {
  return (
    typeof error === "object" &&
    error !== null &&
    "isAxiosError" in error &&
    (error as { isAxiosError: unknown }).isAxiosError === true
  );
};

/**
 * Type guard for Axios error with API error response
 */
export const isAxiosErrorWithResponse = (
  error: unknown,
): error is AxiosError<ApiErrorResponse> => {
  return isAxiosError(error) && error.response !== undefined;
};

/**
 * Check if axios error is a network error (no response)
 */
export const isNetworkError = (error: unknown): boolean => {
  if (!isAxiosError(error)) return false;
  return !error.response && error.code !== "ERR_CANCELED";
};

/**
 * Check if axios error is a timeout
 */
export const isTimeoutError = (error: unknown): boolean => {
  if (!isAxiosError(error)) return false;
  return error.code === "ECONNABORTED" || error.code === "ERR_TIMEOUT";
};

/**
 * Check if axios error is a cancellation
 */
export const isCancelledError = (error: unknown): boolean => {
  if (!isAxiosError(error)) return false;
  return error.code === "ERR_CANCELED";
};

/**
 * Extract HTTP status code from error
 */
export const extractStatusCode = (error: unknown): number | undefined => {
  if (isAxiosError(error)) {
    return error.response?.status;
  }

  if (
    typeof error === "object" &&
    error !== null &&
    "statusCode" in error &&
    typeof (error as { statusCode: unknown }).statusCode === "number"
  ) {
    return (error as { statusCode: number }).statusCode;
  }

  return undefined;
};

/**
 * Extract API error response data from axios error
 */
export const extractApiErrorResponse = (
  error: unknown,
): ApiErrorResponse | undefined => {
  if (!isAxiosErrorWithResponse(error)) return undefined;

  const data = error.response?.data;

  if (isApiErrorResponse(data)) {
    return data;
  }

  return undefined;
};

// /**
//  * Extract validation errors from API response
//  */
// export const extractValidationErrors = (
//   error: unknown,
// ): ValidationFieldError[] => {
//   const apiResponse = extractApiErrorResponse(error);

//   if (!apiResponse?.errors) return [];

//   const fieldErrors: ValidationFieldError[] = [];

//   for (const [field, messages] of Object.entries(apiResponse.errors)) {
//     for (const message of messages) {
//       fieldErrors.push({ field, message });
//     }
//   }

//   return fieldErrors;
// };

/**
 * Extract request info from axios error
 */
export const extractRequestInfo = (
  error: unknown,
): { url?: string; method?: string; headers?: Record<string, string> } => {
  if (!isAxiosError(error)) return {};

  return {
    url: error.config?.url,
    method: error.config?.method?.toUpperCase(),
    headers: error.config?.headers as Record<string, string> | undefined,
  };
};

/**
 * Extract Retry-After header value
 */
export const extractRetryAfter = (error: unknown): number | undefined => {
  if (!isAxiosErrorWithResponse(error)) return undefined;

  const retryAfter = error.response?.headers?.["retry-after"];

  if (!retryAfter) return undefined;

  // Parse as number (seconds)
  const seconds = parseInt(retryAfter, 10);
  if (!isNaN(seconds)) return seconds;

  // Parse as date
  const date = new Date(retryAfter);
  if (!isNaN(date.getTime())) {
    return Math.ceil((date.getTime() - Date.now()) / 1000);
  }

  return undefined;
};
